import React from "react";
import { motion } from "framer-motion";
import SectionWithImage from "./SectionWithImage";
import about1 from "../../assets/images/about1.png";
import about2 from "../../assets/images/about2.png";
import about3 from "../../assets/images/about3.png";

import sec2icon1 from "../../assets/icons/sec2icon1.png";
import sec2icon2 from "../../assets/icons/sec2icon2.png";
import sec2icon3 from "../../assets/icons/sec3icon1.png";

import sec3icon1 from "../../assets/icons/sec3icon1.png";
import sec3icon2 from "../../assets/icons/sec3icon2.png";
import sec3icon3 from "../../assets/icons/sec3icon3.png";

function AboutUs() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#C334F6]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-[#543DF4]/10 rounded-full blur-3xl animate-float-reverse" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#C334F6]/5 to-[#543DF4]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="pt-32 pb-20"
        >
          {/* Who We Are? section */}
          <motion.section 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col lg:flex-row items-center gap-12 mb-20 px-4"
          >
            <motion.div 
              className="lg:w-1/2"
              initial={{ x: -50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <motion.span
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-block px-6 py-2 rounded-full border border-white/30 bg-white/15 backdrop-blur-sm mb-6 text-white"
              >
                About Us
              </motion.span>
              
              <h2 className="text-2xl font-normal leading-[28.8px] tracking-[0.002em] text-transparent bg-clip-text bg-gradient-to-r from-[#C334F6] to-[#543DF4] mb-4">
                Who We Are?
              </h2>
              
              <h1 className="text-5xl font-normal leading-[1.2] tracking-[0.001em] text-white mb-6">
                Empowering Real Estate Agents to
                <br />
                <span className="bg-gradient-to-r from-[#C334F6] to-[#543DF4] bg-clip-text text-transparent">
                  Connect with More Clients
                </span>
              </h1>
              
              <p className="text-xl font-normal leading-[1.5] tracking-[0.002em] text-white/95 mb-6">
                Lead Trek is a cutting edge tool designed to revolutionize the way
                real estate agents find and connect with potential clients. By
                leveraging the power of social media, our innovative platform
                empowers agents to identify and gather leads efficiently and
                effectively.
              </p>
              
              <motion.p 
                className="text-2xl font-medium leading-[1.3] tracking-[0.002em] text-transparent bg-clip-text bg-gradient-to-r from-[#C334F6] to-[#543DF4]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Discover . Connect . Grow .
              </motion.p>
            </motion.div>
            
            <motion.div 
              className="lg:w-1/2"
              initial={{ x: 50, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#C334F6]/20 to-[#543DF4]/20 rounded-2xl blur-2xl" />
                <img
                  src={about1}
                  alt="Real estate agent using Lead Trek"
                  className="w-full h-auto rounded-2xl relative z-10 shadow-2xl"
                />
              </div>
            </motion.div>
          </motion.section>

          {/* Our Vision section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionWithImage
              title="Our Vision"
              imageSrc={about2}
              imagePosition="left"
              points={[
                {
                  icon: sec2icon1,
                  text: "To be the industry leading tool for real estate agents seeking to maximize their lead generation potential through social media.",
                },
                {
                  icon: sec2icon2,
                  text: "Revolutionize the way real estate agents connect with potential clients through social media.",
                },
                {
                  icon: sec2icon3,
                  text: "Empower real estate agents to achieve their goals by providing the most effective lead generation tools available.",
                },
              ]}
            />
          </motion.div>

          {/* Our Mission section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionWithImage
              title="Our Mission"
              imageSrc={about3}
              imagePosition="right"
              points={[
                {
                  icon: sec3icon1,
                  text: "To empower real estate agents with innovative technology that simplifies the process of identifying, attracting, and nurturing leads from social media platforms.",
                },
                {
                  icon: sec3icon2,
                  text: "To foster a community of successful real estate agents who utilize our tool to achieve their goals.",
                },
                {
                  icon: sec3icon3,
                  text: "To provide a user-friendly and efficient platform that delivers exceptional value and drives business growth.",
                },
              ]}
            />
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default AboutUs;
